/**
 * SelectDetailsContent Component
 *
 * Shared form content for selecting which details to enable for a category.
 * Used by both SelectDetailsDialog (desktop) and SelectDetailsPage (mobile).
 */

import React, { ReactElement } from "react";
import {
  Box,
  Stack,
  Typography,
  Alert,
  Paper,
  Chip,
  Divider,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  IconButton,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { TransactionCategory, TransactionCategoryGroup } from "../../../types";

export interface SelectDetailsContentProps {
  category: TransactionCategory;
  group: TransactionCategoryGroup | null;
  enabledDetails: string[];
  setEnabledDetails: (details: string[]) => void;
  defaultValues: Record<string, string | number>;
  setDefaultValues: (values: Record<string, string | number>) => void;
  isDetailUsed: (categoryName: string, detailName: string) => boolean;
}

export function SelectDetailsContent({
  category,
  group,
  enabledDetails,
  setEnabledDetails,
  defaultValues,
  setDefaultValues,
  isDetailUsed,
}: SelectDetailsContentProps): ReactElement {
  return (
    <Box>
      {!group || !group.details || group.details.length === 0 ? (
        <Alert severity="info">
          No details available for this group. Please add details in the group&apos;s &quot;Manage Details&quot; section first.
        </Alert>
      ) : (
        <Stack spacing={2}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="subtitle2">
              Select details to show for this category
            </Typography>
            <Chip
              label={`${enabledDetails.length} selected`}
              size="small"
              color={enabledDetails.length > 0 ? "primary" : "default"}
            />
          </Box>

          <Divider />

          <Box
            sx={{
              maxHeight: "400px",
              overflowY: "auto",
              overflowX: "hidden",
              pr: 1,
            }}
          >
            <Stack spacing={2}>
              {group.details.map((detail) => {
                const isEnabled = enabledDetails.includes(detail.name);
                const defaultValue = defaultValues[detail.name];
                const detailUsed = isDetailUsed(category.name, detail.name);

                return (
                  <Paper
                    key={detail.name}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderColor: isEnabled ? "primary.main" : "divider",
                      backgroundColor: isEnabled ? "action.selected" : "background.paper",
                      opacity: detailUsed && !isEnabled ? 0.6 : 1,
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ width: "100%" }}>
                      <Tooltip
                        title={
                          detailUsed && isEnabled
                            ? `Cannot disable this detail: It is used in one or more transactions for category "${category.name}"`
                            : ""
                        }
                        arrow
                        placement="top"
                      >
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={isEnabled}
                                disabled={detailUsed && isEnabled}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  if (checked) {
                                    setEnabledDetails([...enabledDetails, detail.name]);
                                    if (detail.type === "measurable") {
                                      setDefaultValues({
                                        ...defaultValues,
                                        [detail.name]: "",
                                      });
                                    }
                                  } else {
                                    if (!detailUsed) {
                                      setEnabledDetails(enabledDetails.filter((d) => d !== detail.name));
                                      const newDefaults = { ...defaultValues };
                                      delete newDefaults[detail.name];
                                      setDefaultValues(newDefaults);
                                    }
                                  }
                                }}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {detail.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Type: {detail.type === "list" ? "List" : "Measurable"}
                                  {detail.type === "list" && detail.listItems && detail.listItems.length > 0 && (
                                    <> • {detail.listItems.length} items</>
                                  )}
                                  {detail.type === "measurable" && detail.unit && (
                                    <> • Unit: {detail.unit}</>
                                  )}
                                </Typography>
                              </Box>
                            }
                            sx={{ flex: 1, minWidth: 0 }}
                          />
                        </span>
                      </Tooltip>

                      {isEnabled && (
                        <Box sx={{ width: 220, flexShrink: 0, display: "flex", alignItems: "center" }}>
                          {detail.type === "list" && detail.listItems && detail.listItems.length > 0 && (
                            <FormControl fullWidth size="small" variant="outlined">
                              <InputLabel id={`default-option-label-${detail.name}`}>
                                Default Option
                              </InputLabel>
                              <Select
                                labelId={`default-option-label-${detail.name}`}
                                value={defaultValue ?? ""}
                                displayEmpty
                                renderValue={(v) => (v === "" ? "Default Option" : v)}
                                onChange={(e) => {
                                  const newValue = e.target.value;
                                  setDefaultValues({
                                    ...defaultValues,
                                    [detail.name]: newValue === "" ? "" : newValue,
                                  });
                                }}
                                label="Default Option"
                                MenuProps={{
                                  disablePortal: true,
                                  MenuListProps: {
                                    onWheel: (e: React.WheelEvent) => {
                                      const listbox = e.currentTarget;
                                      if (listbox.scrollHeight <= listbox.clientHeight) return;
                                      e.stopPropagation();
                                      e.preventDefault();
                                      listbox.scrollTop += e.deltaY;
                                    },
                                  },
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    borderWidth: "1px",
                                    borderColor: "rgba(0, 0, 0, 0.23)",
                                  },
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "rgba(0, 0, 0, 0.87)",
                                  },
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderWidth: "2px",
                                    borderColor: "primary.main",
                                  },
                                }}
                              >
                                <MenuItem value="">
                                  <em>None</em>
                                </MenuItem>
                                {detail.listItems.map((item) => (
                                  <MenuItem key={item} value={item}>
                                    {item}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}

                          {detail.type === "measurable" && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%" }}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Default Value"
                                placeholder="Enter value"
                                value={
                                  typeof defaultValue === "string"
                                    ? defaultValue
                                    : typeof defaultValue === "number"
                                    ? String(defaultValue)
                                    : ""
                                }
                                onChange={(e) => {
                                  setDefaultValues({
                                    ...defaultValues,
                                    [detail.name]: e.target.value,
                                  });
                                }}
                                type="text"
                              />
                              <Tooltip title="Clear default">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    const newDefaults = { ...defaultValues };
                                    delete newDefaults[detail.name];
                                    setDefaultValues(newDefaults);
                                  }}
                                  aria-label="Clear default value"
                                  sx={{ flexShrink: 0 }}
                                >
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          </Box>
        </Stack>
      )}
    </Box>
  );
}
